<script setup lang="ts">
import SideBarUtils from "@/utils/common/SideBarUtils.ts";
import Translator from "@/utils/common/Translator.ts";
import BlockUtils from "@/utils/common/BlockUtils.ts";
import EditViewUtils, {EditViewState} from "@/utils/editView/EditViewUtils.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import EditViewToolbar from "@/components/EditView/EditViewToolbar.vue";
import {QScrollArea, QTabPanel, Screen} from "quasar";
import EditScreenDiscussionContainer from "@/components/EditView/editScreens/EditScreenDiscussionContainer.vue";
import TreeView from "@/components/TreeView.vue";
import EditViewBlock from "@/components/EditView/EditViewBlock.vue";
import {allFieldsKey, EditViewLayoutProps, isPageChangedKey} from "@/utils/presenters/interfaces.ts";
import {computed, nextTick, onMounted, onUnmounted, provide, ref, toValue, useTemplateRef, watch} from "vue";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {addRefToList, commonNavigateToNext, NamaNavigationArgs, NamaVueWidgetContainer} from "@/utils/common/NamaVueWidget.ts";
import EditUiElementPropertiesBtn from "@/components/EditView/EditUIElementPropertiesBtn.vue";
import NamaIconUtils from "@/utils/common/NamaIconUtils.ts";
import {useEventListener} from "@vueuse/core";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import ShortCutsUtil from "@/utils/common/ShortCutsUtil.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import TopPanelSearchUtils, {FieldToFocusSearchItem} from "@/utils/common/TopPanelSearchUtils.ts";
import FieldToFocusUtils from "@/utils/common/FieldToFocusUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import EditScreenBlock = namaTypes.EditScreenBlock;
import TemplateMetadata = namaTypes.TemplateMetadata;

const props = defineProps<EditViewLayoutProps>();
const presenter: EditPresenter = props.presenter;
const showTree = ref<boolean>(presenter.place.showTree ?? false);
const pageIndex = defineModel<number>("pageIndex", {required: false, default: 0});
const {compactMode, darkMode, isUltra} = storeToRefs(useNamaFlagsStore());
const blocksByPageIndex: Record<number, NamaVueWidgetContainer[]> = {}
const toolbar = ref<typeof EditViewToolbar | null>(null);
const qScrollAreaRef = useTemplateRef<QScrollArea[]>("qScrollAreaRef");
const blocksContainerRef = useTemplateRef<HTMLDivElement>("blocksContainerRef");

const allFields = ref<FieldToFocusSearchItem[] | undefined>()
provide(allFieldsKey, allFields)
const isPageChanged = ref(true)
provide(isPageChangedKey, isPageChanged)
const fieldToFocusHandler = new FieldToFocusUtils(presenter)
const {
  fieldToFocusOptions,
  fieldsHelpMessages,
  showHelpMessagesActive
} = storeToRefs(useNamaFlagsStore());
const states = computed<EditViewState[]>(() => {
  const states: EditViewState[] = [];
  const record = presenter.fetchRecord();
  if (!record)
    return states;
  const currentStates = EditViewUtils.getCurrentStates(record, props.place);
  if (currentStates.length)
    states.push(...currentStates);
  else if (presenter.clone)
    states.push(...EditViewUtils.getCurrentStates(presenter.clone, props.place))
  return states
});

async function toggleTreeForEditView() {
  showTree.value = !showTree.value;
  presenter.place.showTree = showTree.value;
  await presenter.updateRouterFromPlaceAndSkipNextRouteRefreshAndQuickCreator();
}

const firstVisibleGroup = computed(() => {
  let blocks = presenter.pages.value[0].blocks ?? [];
  for (let blockIndex = 0; blockIndex < blocks.length; blockIndex++) {
    if (blocks[blockIndex]['namaClassTypeId'] === 'EditScreenGroup')
      return blockIndex;
  }
  return 0;
})

function onRef(widget: any, pageIndex: number, blockIndex: number) {
  if (!blocksByPageIndex[pageIndex])
    blocksByPageIndex[pageIndex] = [];
  addRefToList(blocksByPageIndex[pageIndex], {widget, index: blockIndex});
}

function onNavigateToNext(args: NamaNavigationArgs, pageIndex: number, blockIndex: number) {
  let pageBlocks = blocksByPageIndex[pageIndex];
  commonNavigateToNext(pageBlocks, args, blockIndex, () => {
    if (args.keyboardEvent.shiftKey) {
      commonNavigateToNext(pageBlocks, args, pageBlocks.length, () => {
      })
    } else {
      commonNavigateToNext(pageBlocks, args, -1, () => {
      })
    }
  });
}

function onTabChanged() {
  setTimeout(() => {
    isPageChanged.value = true;
  }, 100)
}

function onBeforeTabChanged() {
  isPageChanged.value = false;
}

function shouldShowMainFile(pageIndex: number, blockIndex: number): boolean {
  if (props.place.quickCreate)
    return false;
  if (pageIndex > 0)
    return false;
  if (blockIndex !== firstVisibleGroup.value)
    return false;
  return presenter.shouldAddMainFileWidget();
}

function isCriteriaBlock(block: EditScreenBlock) {
  return block['namaClassTypeId'] === 'EditScreenBlock' && EditViewUtils.isCriteriaDefinitionCriteria(presenter.metadata?.owner, block.fieldId);
}

function isCellInputElement(cell: Element | null): boolean {
  if (!cell)
    return false;
  if (cell.classList.contains("ag-cell") || cell instanceof HTMLInputElement)
    return true;
  return false;
}

useEventListener("keydown", function (e: KeyboardEvent) {
  const cell: Element | null = document.activeElement;
  if (FieldWidgetUtil.focusedField || isCellInputElement(cell))
    return;
  const scrollRef = qScrollAreaRef.value ? qScrollAreaRef.value[pageIndex.value ?? 0] : undefined;
  if (!scrollRef)
    return;
  const currentTop = scrollRef.getScrollPosition().top;
  let nextTop: number;
  switch (e.code) {
    case "ArrowDown":
      nextTop = currentTop + 30;
      break;
    case "ArrowUp":
      nextTop = currentTop - 30;
      break;
    case "PageDown":
      nextTop = currentTop + scrollRef.getScroll().verticalContainerSize;
      break;
    case "PageUp":
      nextTop = currentTop - scrollRef.getScroll().verticalContainerSize;
      break;
    case "Home":
      nextTop = 0;
      break;
    case "End":
      nextTop = scrollRef.getScroll().verticalSize;
      break;
    default:
      return;
  }
  e.preventDefault();
  scrollRef.setScrollPosition("vertical", nextTop);
})

watch(showHelpMessagesActive, async () => {
  if (ObjectChecker.isNotEmptyOrNull(fieldsHelpMessages.value[presenter.entityType]))
    return;
  await GUIUtilities.fetchFieldsHelpMessages(presenter.entityType)
  const firstFieldHasHelpMsg = fieldsHelpMessages.value?.[presenter.entityType]?.[0]?.fieldId
  allFields.value = TopPanelSearchUtils.searchForAllFields();
  const fieldToStepInto: FieldToFocusSearchItem | undefined = allFields.value.find(field => field.id == firstFieldHasHelpMsg);
  useNamaFlagsStore().setFieldToFocusOptions({
    field: fieldToStepInto,
    dontFocus: true
  })
})

watch(() => fieldToFocusOptions.value.field, async (searchItem) => {
  if (!searchItem || !fieldToFocusOptions.value)
    return;
  if ((searchItem.presenterId && searchItem.presenterId !== presenter.presenterId))
    return;
  let extraData = searchItem.record?.extraData!
  const pageTitle = extraData.pageTitle
  const index = toValue(presenter.pages).findIndex(page => page.title?.resourceId === pageTitle);
  if (pageIndex.value !== index) {
    pageIndex.value = index;
    if (!BlockUtils.blockIsEditScreenGrid(extraData.block))
      await new Promise(resolve => setTimeout(resolve, 250))
  }
  await nextTick()
  if (!BlockUtils.blockIsEditScreenGrid(extraData.block)) {
    await fieldToFocusHandler.focusToFieldHandler(searchItem, fieldToFocusOptions.value)
  }
})

const searcher = (searchValue) => fieldToFocusHandler.fieldSearcher(searchValue, presenter)
const editPresenterRetriever = () => presenter;

function confirmClosingWindow(event) {
  if (presenter.isDirty()) {
    const msg = Translator.translateId('areYouToNavigate');
    event.returnValue = msg;
    return msg;
  }
}

onMounted(() => {
  window.addEventListener('beforeunload', confirmClosingWindow);
  TopPanelSearchUtils.pushFieldSearcher(searcher)
  TopPanelSearchUtils.pushEditPresenterRetriever(editPresenterRetriever)
  let templates: TemplateMetadata[] = ClientCache.getEntityTemplates(presenter.entityType);
  if (templates) {
    for (let entityTemplate of templates) {
      if (entityTemplate && entityTemplate.shortcut && entityTemplate.template && entityTemplate.template.id) {
        ShortCutsUtil.registerShortcut_(entityTemplate.shortcut, "template_" + entityTemplate.template.id)
      }
    }
  }
})
onUnmounted(() => {
  window.removeEventListener('beforeunload', confirmClosingWindow);
  presenter.onVueUnmounted();
  TopPanelSearchUtils.popFieldSearcher(searcher)
  TopPanelSearchUtils.popEditPresenterRetriever(editPresenterRetriever)
  let templates: TemplateMetadata[] = ClientCache.getEntityTemplates(presenter.entityType);
  if (templates) {
    for (let entityTemplate of templates) {
      if (entityTemplate && entityTemplate.shortcut && entityTemplate.template && entityTemplate.template.id) {
        ShortCutsUtil.unregisterShortcut_(entityTemplate.shortcut, "template_" + entityTemplate.template.id)
      }
    }
  }
})

defineExpose({toolbar, title: presenter.getEditViewTitle(), states, blocksContainerRef});
</script>

<template>
  <div class="tw-h-full tw-w-full tw-flex tw-flex-col">
    <div v-if="showTitle && (!isUltra || Screen.lt.sm)"
         class="tw-flex tw-ps-3 tw-items-center tw-gap-2 tw-text-namaFontColor-header-light dark:tw-text-namaFontColor-header-dark tw-text-xl tw-font-bold tw-pb-3 mobile:tw-pb-2 compact:tw-pb-0 ultra:tw-pb-0 mobile:compact:tw-pb-0 mobile:ultra:tw-pb-0">
      <div class="tw-shrink-0 tw-flex tw-items-center tw-gap-1">
        <NamaIcon :constant="presenter.entityType" default-icon="nm-monitoring" :menu-item="SideBarUtils.findMenuItem(place.src)?.item"
                  class="tw-size-7 tw-text-[28px]"/>
        <span v-text="presenter.getEditViewTitle().value"/>
      </div>
      <div class="tw-flex tw-items-center tw-gap-2 tw-flex-wrap" v-if="Boolean(states?.length)">
        <div v-for="state in states">
          <div :style="{backgroundColor:NamaIconUtils.toColorCode({constant:state.status,defaultColor:'gray'})}"
               class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-1.5 tw-py-0.5 tw-rounded tw-opacity-90 hover:tw-opacity-100 active-white">
            <NamaIcon default-icon="nm-status_8917343" :constant="state.status" class="!tw-size-[15px] !tw-text-[16px]"/>
            <span v-text="Translator.translateId(state.label)" class="tw-text-sm tw-whitespace-nowrap tw-text-white/90 tw-font-semibold"/>
          </div>
        </div>
      </div>
    </div>
    <div
      class="tw-h-full tw-w-full tw-flex tw-flex-row tw-px-3 tw-pt-1 ultra:tw-pt-0 tw-my-2 compact:tw-my-1 ultra:tw-my-0.5 tw-gap-2 tw-transition-all tw-ease-in-out tw-duration-1000">
      <TreeView v-if="showTree" :presenter="presenter" :showTree="showTree"/>
      <q-card
        class="tw-grow tw-w-full tw-flex tw-flex-col tw-shadow-none tw-bg-transparent tw-rounded-lg">
        <div
          class="tw-h-fit tw-top-0 tw-rounded-b-lg tw-bg-namaBackground-deep-light dark:tw-bg-namaBackground-deep-dark tw-pb-0 compact:tw-px-0.5 ultra:tw-px-0">
          <div
            class="tw-bg-namaBackground-dim-light dark:tw-bg-namaBackground-dim-dark tw-py-1.5 compact:tw-py-1 ultra:tw-py-0 tw-px-2.5 compact:tw-px-1.5 ultra:tw-px-0.5 tw-rounded-t-lg">
            <div class="tw-flex tw-flex-row tw-items-center tw-w-full tw-gap-2">
              <div v-if="showTitle && isUltra && !Screen.lt.sm"
                   class="tw-flex tw-items-center tw-gap-2 tw-max-w-[50%] tw-overflow-x-auto tw-shrink-0
                          tw-text-namaFontColor-header-light dark:tw-text-namaFontColor-header-dark tw-text-base tw-font-bold tw-whitespace-nowrap tw-ps-1">
                <div class="tw-shrink-0 tw-flex tw-items-center tw-gap-1">
                  <NamaIcon :constant="presenter.entityType" default-icon="nm-monitoring" :menu-item="SideBarUtils.findMenuItem(place.src)?.item"
                            class="tw-size-7 tw-text-[28px]"/>
                  <span v-text="presenter.getEditViewTitle().value"/>
                </div>
                <div class="tw-flex tw-items-center tw-gap-2 tw-flex-nowrap" v-if="Boolean(states?.length)">
                  <div v-for="state in states">
                    <div :style="{backgroundColor:NamaIconUtils.toColorCode({constant:state.status,defaultColor:'gray'})}"
                         class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-1.5 tw-py-0.5 tw-rounded tw-opacity-90 hover:tw-opacity-100 active-white">
                      <NamaIcon default-icon="nm-status_8917343" :constant="state.status" class="!tw-size-[15px] !tw-text-[16px]"/>
                      <span v-text="Translator.translateId(state.label)" class="tw-text-sm tw-whitespace-nowrap tw-text-white/90 tw-font-semibold"/>
                    </div>
                  </div>
                </div>
              </div>
              <EditViewToolbar
                class="tw-flex-1 tw-min-w-0"
                :presenter="presenter" :alwaysHideLabels="alwaysHideLabels" ref="toolbar"
                @toggleTreeForEditView="toggleTreeForEditView"/>
            </div>
          </div>
          <q-tabs
            v-model="pageIndex"
            content-class="edit-view-tab-content tw-text-namaFontColor-header-light dark:tw-text-namaFontColor-header-dark tw-pt-0 tw-px-2.5 compact:tw-px-1 ultra:tw-px-0.5 mobile:tw-px-2.5"
            indicator-color="primary"
            align="left"
            :dense="compactMode"
            narrow-indicator
            :no-caps="true"
          >
            <q-separator v-if="!compactMode"/>
            <q-tab
              v-for="(page, pageIndex) in presenter.pages.value" :key="pageIndex"
              :name="pageIndex">
              <template #default>
                <div class="tw-flex tw-items-center tw-gap-2">
                  <EditUiElementPropertiesBtn :blockOrField="page" :entity-type="presenter.entityType"/>
                  <NamaIcon :block="page" :presenter="presenter" class="tw-size-5"/>
                  <span v-text="Translator.translateNaMaText(page.title, presenter.entityType)"/>
                </div>
              </template>
            </q-tab>
          </q-tabs>
        </div>
        <q-tab-panels @before-transition="onBeforeTabChanged" @transition="onTabChanged" keep-alive animated v-model="pageIndex"
                      class="tw-grow tw-bg-transparent tw-rounded-lg">
          <q-tab-panel v-for="(page, pageIndex) in presenter.pages.value" :key="pageIndex" :name="pageIndex"
                       class="tw-h-full tw-w-full tw-p-0 tw-pt-4 compact:tw-p-0 ultra:tw-p-0 compact:tw-pt-1 ultra:tw-pt-0.5"
                       :data-page-title="page.title?.resourceId">
            <q-scroll-area ref="qScrollAreaRef" :dark="darkMode" class="tw-h-full tw-w-full dir-ltr nama-scroll-area-content">
              <div ref="blocksContainerRef" :dir="Translator.fetchDir()"
                   class="tw-flex tw-flex-wrap tw-w-full tw-h-full tw-gap-3.5 compact:tw-gap-1 ultra:tw-gap-0.5">
                <template v-for="(block, blockIndex) in page.blocks">
                  <template v-if="BlockUtils.isValidBlockForAttach(presenter,block)">
                    <EditScreenDiscussionContainer v-if="block['namaClassTypeId'] === 'EditScreenBlock' && block.fieldId === 'discussions'"
                                                   :block="block" :presenter="presenter"
                                                   :blockIndex="blockIndex" :pageIndex="pageIndex" :collapsed="false"
                                                   @navigateToNext="(args)=>onNavigateToNext(args, pageIndex, blockIndex)"
                                                   :ref="(r)=>onRef(r, pageIndex, blockIndex)"/>
                    <EditViewBlock v-else
                                   :block=" isCriteriaBlock(block) ? EditViewUtils.fetchCriteriaBlock(): block" :presenter="presenter"
                                   :blockIndex="blockIndex" :pageIndex="pageIndex" :ref="(r)=>onRef(r, pageIndex, blockIndex)"
                                   @navigateToNext="(args)=>onNavigateToNext(args, pageIndex, blockIndex)"
                                   :showMainFile="shouldShowMainFile(pageIndex,blockIndex)"/>
                  </template>
                </template>
              </div>
            </q-scroll-area>
          </q-tab-panel>
        </q-tab-panels>
      </q-card>
    </div>
  </div>
</template>

<style scoped>

</style>
